import React, { useEffect, useState, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
interface OpeningCurtainProps {
  onComplete: () => void;
}
export function OpeningCurtain({
  onComplete
}: OpeningCurtainProps) {
  const prefersReducedMotion = useReducedMotion();
  const [isDone, setIsDone] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    // Total animation time
    const duration = prefersReducedMotion ? 400 : 2600;
    const timer = setTimeout(() => {
      setIsDone(true);
      onComplete();
    }, duration);
    return () => clearTimeout(timer);
  }, [onComplete, prefersReducedMotion]);
  // Particle system for the molten gold edge
  useEffect(() => {
    if (prefersReducedMotion || isDone) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationFrameId: number;
    let startTime = performance.now();
    // Resize canvas to match window
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize);
    resize();
    const particles: {
      x: number;
      y: number;
      vx: number;
      vy: number;
      life: number;
      maxLife: number;
      size: number;
    }[] = [];
    const render = (time: number) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / 2200, 1); // Edge moves over 2.2s
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Current x position of the wipe edge (from 0 to 100% of width)
      // We use an easeInOut cubic curve for the wipe
      const easeInOut = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const edgeX = easeInOut(progress) * canvas.width;
      // Spawn new particles along the edge
      if (progress < 1 && Math.random() > 0.2) {
        for (let i = 0; i < 3; i++) {
          particles.push({
            x: edgeX + (Math.random() * 20 - 10),
            y: Math.random() * canvas.height,
            vx: Math.random() * 2 - 0.5,
            vy: (Math.random() - 0.5) * 2,
            life: 1,
            maxLife: 0.5 + Math.random() * 1,
            size: Math.random() * 2 + 1
          });
        }
      }
      // Update and draw particles
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.life -= 0.016; // approx 60fps
        if (p.life <= 0) {
          particles.splice(i, 1);
          continue;
        }
        const alpha = p.life / p.maxLife;
        ctx.fillStyle = `rgba(201, 168, 76, ${alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      if (progress < 1.2) {
        // Keep rendering particles a bit after edge passes
        animationFrameId = requestAnimationFrame(render);
      }
    };
    animationFrameId = requestAnimationFrame(render);
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [prefersReducedMotion, isDone]);
  if (prefersReducedMotion) {
    return <motion.div className="fixed inset-0 z-[100] bg-brand-black" initial={{
      opacity: 1
    }} animate={{
      opacity: 0
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} onAnimationComplete={() => onComplete()} data-id="element-116" />;
  }
  return <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden" data-id="element-117">
      {/* The solid black curtain wiping left to right */}
      <motion.div className="absolute inset-0 bg-brand-black" initial={{
      x: '0%'
    }} animate={{
      x: '100%'
    }} transition={{
      duration: 2.2,
      ease: [0.76, 0, 0.24, 1]
    }} data-id="element-118">
        {/* The molten gold glowing edge on the right side of the moving curtain */}
        <div className="absolute top-0 right-0 bottom-0 w-8 translate-x-1/2 flex" data-id="element-119">
          <div className="w-full h-full bg-gradient-to-r from-brand-gold to-[#E8C97A] blur-[8px] opacity-80" data-id="element-120" />
          <div className="absolute inset-0 bg-brand-gold blur-[2px] opacity-100" data-id="element-121" />
        </div>
      </motion.div>

      {/* Canvas for particles */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" data-id="element-122" />
    </div>;
}